import {
    ApplicationConfig,
    inject,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection
} from '@angular/core';
import {provideRouter} from '@angular/router';
import {routes} from './app.routes';
import {HttpClient, provideHttpClient, withInterceptors,} from "@angular/common/http";
import {provideTranslateService} from "@ngx-translate/core";
import {provideTranslateHttpLoader} from "@ngx-translate/http-loader";
import {ConfigurationService} from "./shared/services/configuration.service";
import {DatePipe} from "@angular/common";
import {provideKeycloakAngular} from "./keycloak/keycloak.config";
import {includeBearerTokenInterceptor} from "keycloak-angular";
import {firstValueFrom} from "rxjs";
import {errorInterceptor} from "./shared/interceptors/error.interceptor";
import {STORAGE_CONFIG} from "./shared/services/local-storage.service";
import {STORAGE_KEYS} from "./shared/constants/storage-keys";
import {NGX_MONACO_EDITOR_CONFIG} from "ngx-monaco-editor-v2";
import {providePrimeNG} from "primeng/config";
import {ConfirmationService, MessageService} from "primeng/api";
import {DialogService} from "primeng/dynamicdialog";
import DATA_UNIT_THEME from "../../public/theme/dataunit-theme";
import {
    LucideArrowLeftRight,
    LucideCodeXml,
    LucideLanguages,
    LucideLogOut,
    LucideMenu,
    LucideSearch,
    LucideSettings,
    provideLucideIcons,
} from "@lucide/angular";

export const provideLocalStorage = () => ({
    provide: STORAGE_CONFIG,
    useValue: {namespace: 'config', mode: 'blob', blobKey: 'settings'}
});

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({eventCoalescing: true}),
        providePrimeNG({
            theme: {preset: DATA_UNIT_THEME, options: {darkModeSelector: '.p-dark'}},
        }),
        provideLucideIcons(
            LucideMenu,
            LucideLogOut,
            LucideArrowLeftRight,
            LucideLanguages,
            LucideSearch,
            LucideSettings,
            LucideCodeXml,
        ),
        MessageService,
        DialogService,
        ConfirmationService,
        provideRouter(routes),
        provideAppInitializer(() => {
            const http = inject(HttpClient);
            return firstValueFrom(inject(ConfigurationService).loadConfig(http));
        }),
        provideHttpClient(withInterceptors([includeBearerTokenInterceptor, errorInterceptor])),
        provideKeycloakAngular({
            url: 'https://login.dataunit.ch/auth',
            realm: (() => {
                const REALM_PATTERN = /^[a-zA-Z0-9._-]+$/;
                const EMAIL_PATTERN = /^[\p{L}\p{N}._+-]+@[\p{L}\p{N}-]+(\.[\p{L}\p{N}-]+)+$/u;

                const urlParams = new URLSearchParams(globalThis.location.search);
                const realmParam = urlParams.get('realm');
                const emailParam = urlParams.get('email');
                const realm = realmParam && REALM_PATTERN.test(realmParam) ? realmParam : null;
                const email = emailParam && EMAIL_PATTERN.test(emailParam) ? emailParam : null;

                let storage: any;
                const rawStorage = localStorage.getItem("config:settings");
                storage = rawStorage ? JSON.parse(rawStorage) : {};

                if (realm) {
                    storage[STORAGE_KEYS.REALM] = realm;
                    if (email) storage[STORAGE_KEYS.MAIL] = email;
                    localStorage.setItem("config:settings", JSON.stringify(storage));
                    return realm;
                }

                return storage[STORAGE_KEYS.REALM] ?? '';
            })(),
            clientId: 'licenseportal',
        }),
        provideLocalStorage(),
        provideTranslateService({
            loader: provideTranslateHttpLoader({prefix: "assets/i18n/"}),
            fallbackLang: 'en',
        }),
        DatePipe,
        {
            provide: NGX_MONACO_EDITOR_CONFIG,
            useValue: {
                baseUrl: 'assets/monaco/vs',
                defaultOptions: { scrollBeyondLastLine: false },
                onMonacoLoad: () => {
                    const dark = document.documentElement.classList.contains('p-dark');
                    (globalThis as any).monaco?.editor?.setTheme(dark ? 'vs-dark' : 'vs');
                }
            }
        }
    ]
};
